import { useEffect, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";

import { authValidationStateAtom, userAtom } from "@/atoms/auth.atoms";
import { participantIdAtom, romIdAtom, romPhaseAtom } from "@/atoms/rom.atoms";
import { HttpError } from "@/services/api";
import { romService } from "@/services/rom.service";
import { getGuestDeviceId } from "@/services/signaling.client";

/**
 * Validates on mount whether the current session still corresponds to an
 * active ROM where this device is an active participant. If the backend has
 * no record (404) or the rom is closed, marks the session as "invalid" which
 * the auth flow treats as a logout + session-expired event.
 *
 * Trigger is `user` (not the legacy accessKey atom) so it fires correctly
 * in both browser (cookie path, no accessKey) and Electron (header path).
 *
 * If valid, populates romId/participantId/phase atoms so heartbeat can run.
 */
export function useInitialAuthValidation() {
  const user = useAtomValue(userAtom);
  const setValidationState = useSetAtom(authValidationStateAtom);
  const setRomId = useSetAtom(romIdAtom);
  const setParticipantId = useSetAtom(participantIdAtom);
  const setPhase = useSetAtom(romPhaseAtom);

  const ranRef = useRef(false);

  const runValidation = useRef<() => void>(() => {});

  useEffect(() => {
    runValidation.current = () => {
      if (!user) {
        setValidationState("valid");
        setRomId(null);
        setParticipantId(null);
        setPhase("pairing");
        return;
      }

      const deviceId = getGuestDeviceId();

      romService
        .getActiveForDevice(deviceId)
        .then((result) => {
          if (!result) {
            // No active rom — treat as "not paired yet", not a session failure.
            // (Backend now returns null for this case instead of 404.)
            setRomId(null);
            setParticipantId(null);
            setPhase("pairing");
            setValidationState("valid");
            return;
          }
          setRomId(result.rom.id);
          setParticipantId(result.participant.id);
          setPhase(result.participant.phase);
          setValidationState("valid");
        })
        .catch((err) => {
          // Tolerate the legacy 404 from older backend deployments while
          // the rolling redeploy lands.
          if (err instanceof HttpError && err.status === 404) {
            setRomId(null);
            setParticipantId(null);
            setPhase("pairing");
            setValidationState("valid");
            return;
          }
          setValidationState("valid");
          console.warn("[useInitialAuthValidation] validation skipped:", err);
        });
    };
  }, [user, setValidationState, setRomId, setParticipantId, setPhase]);

  useEffect(() => {
    if (!user) {
      runValidation.current();
      ranRef.current = false;
      return;
    }

    if (ranRef.current) return;
    ranRef.current = true;
    runValidation.current();
  }, [user]);

  // Re-run when the emitter announces a rom attached post-pairing
  useEffect(() => {
    const handler = () => runValidation.current();
    window.addEventListener("rom:attached", handler);
    return () => window.removeEventListener("rom:attached", handler);
  }, []);
}
